import React, { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { useRFIStatusSummary, useRFIs, useRFIWidgetCounts } from '../hooks';
import {
  RFI_STATUS_LABELS,
  RFI_PRIORITY_LABELS,
  type RFIPriority,
  type RFIStatus,
} from '../types';

const STATUS_COLORS: Record<RFIStatus, string> = {
  DRAFT: '#9ca3af',
  OPEN: '#3b82f6',
  PENDING_RESPONSE: '#f59e0b',
  ANSWERED: '#22c55e',
  CLOSED: '#6b7280',
};

const PRIORITY_BAR_COLORS: Record<RFIPriority, string> = {
  URGENT: 'bg-red-500',
  HIGH: 'bg-orange-500',
  NORMAL: 'bg-blue-500',
  LOW: 'bg-slate-400',
};

const PRIORITY_ORDER: RFIPriority[] = ['URGENT', 'HIGH', 'NORMAL', 'LOW'];
const STATUS_ORDER: RFIStatus[] = ['DRAFT', 'OPEN', 'PENDING_RESPONSE', 'ANSWERED', 'CLOSED'];

function DonutChart({
  segments,
  size = 96,
  strokeWidth = 14,
}: {
  segments: { value: number; color: string; label: string }[];
  size?: number;
  strokeWidth?: number;
}) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const total = segments.reduce((sum, seg) => sum + seg.value, 0);

  if (total === 0) {
    return (
      <svg width={size} height={size} className="mx-auto">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
        />
      </svg>
    );
  }

  let offset = 0;
  const arcs = segments.map((seg) => {
    const pct = seg.value / total;
    const dash = pct * circumference;
    const gap = circumference - dash;
    const currentOffset = offset;
    offset += dash;
    return (
      <circle
        key={seg.label}
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={seg.color}
        strokeWidth={strokeWidth}
        strokeDasharray={`${dash} ${gap}`}
        strokeDashoffset={-currentOffset}
        strokeLinecap="butt"
      />
    );
  });

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      shapeRendering="geometricPrecision"
    >
      <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>{arcs}</g>
    </svg>
  );
}

function GaugeChart({
  percent,
  size = 64,
  strokeWidth = 9,
  color = '#22c55e',
}: {
  percent: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
}) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = (percent / 100) * circumference;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      shapeRendering="geometricPrecision"
    >
      <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={`${dash} ${circumference - dash}`}
          strokeLinecap="round"
        />
      </g>
    </svg>
  );
}

function DashboardSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-tour-id="rfis-widgets">
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i}>
          <CardHeader className="pb-2">
            <div className="h-3.5 w-28 animate-pulse rounded bg-muted" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

interface Props {
  projectId: number | undefined;
}

export const RfiWidgets: React.FC<Props> = ({ projectId }) => {
  const { data: summary, isLoading: summaryLoading } = useRFIStatusSummary(projectId);
  const { data: counts } = useRFIWidgetCounts(projectId);
  // Unfiltered list drives the priority breakdown so it stays stable as the
  // page-level filters change. React Query dedupes this query.
  const { data: items, isLoading: itemsLoading } = useRFIs(projectId, {});

  const statusCounts = useMemo(() => {
    const map: Record<RFIStatus, number> = {
      DRAFT: 0,
      OPEN: 0,
      PENDING_RESPONSE: 0,
      ANSWERED: 0,
      CLOSED: 0,
    };
    (summary ?? []).forEach((entry) => {
      map[entry.status] = entry.count;
    });
    return map;
  }, [summary]);

  const priorityCounts = useMemo(() => {
    const map: Record<RFIPriority, number> = { URGENT: 0, HIGH: 0, NORMAL: 0, LOW: 0 };
    (items ?? []).forEach((item) => {
      map[item.priority] = (map[item.priority] ?? 0) + 1;
    });
    return map;
  }, [items]);

  if (summaryLoading || itemsLoading) return <DashboardSkeleton />;

  const totalItems = STATUS_ORDER.reduce((sum, status) => sum + statusCounts[status], 0);
  const donutSegments = STATUS_ORDER.map((status) => ({
    value: statusCounts[status],
    color: STATUS_COLORS[status],
    label: RFI_STATUS_LABELS[status],
  }));

  const openItems = statusCounts.DRAFT + statusCounts.OPEN + statusCounts.PENDING_RESPONSE;
  const resolvedItems = statusCounts.ANSWERED + statusCounts.CLOSED;
  const resolvedPercent = totalItems === 0 ? 0 : (resolvedItems / totalItems) * 100;
  const maxPriorityCount = Math.max(...PRIORITY_ORDER.map((p) => priorityCounts[p]), 1);
  const hasPriorityData = PRIORITY_ORDER.some((p) => priorityCounts[p] > 0);
  const overdue = counts?.overdue_rfis ?? 0;
  const awaiting = counts?.awaiting_my_response ?? 0;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-tour-id="rfis-widgets">
      {/* Card 1 – Status Overview */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-xs font-medium text-muted-foreground">
            Status Overview
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-3">
          <DonutChart segments={donutSegments} size={96} strokeWidth={14} />
          <div className="flex flex-wrap justify-center gap-x-3 gap-y-1">
            {donutSegments.map((seg) => (
              <span
                key={seg.label}
                className="flex items-center gap-1 text-[11px] text-muted-foreground"
              >
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: seg.color }}
                />
                {seg.label} ({seg.value})
              </span>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Card 2 – RFIs by Priority */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-xs font-medium text-muted-foreground">
            RFIs by Priority
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col justify-center gap-2.5 py-3">
          {!hasPriorityData ? (
            <div className="py-4 text-xs text-muted-foreground">No RFIs to break down yet.</div>
          ) : (
            PRIORITY_ORDER.map((priority) => {
              const count = priorityCounts[priority];
              return (
                <div key={priority} className="flex items-center gap-2 text-[11px]">
                  <span className="w-14 shrink-0 text-muted-foreground">
                    {RFI_PRIORITY_LABELS[priority]}
                  </span>
                  <div className="relative h-2.5 flex-1 rounded-full bg-muted">
                    <div
                      className={`absolute inset-y-0 left-0 rounded-full ${PRIORITY_BAR_COLORS[priority]}`}
                      style={{ width: `${(count / maxPriorityCount) * 100}%` }}
                    />
                  </div>
                  <span className="w-5 text-right font-semibold tabular-nums">{count}</span>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Card 3 – Resolved Rate */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-xs font-medium text-muted-foreground">Resolved Rate</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center gap-1 py-3">
          <div className="flex items-center gap-3">
            <GaugeChart percent={resolvedPercent} size={64} strokeWidth={9} />
            <span className="text-3xl font-bold tabular-nums">{resolvedPercent.toFixed(1)}%</span>
          </div>
          <span className="text-xs text-muted-foreground">
            across {totalItems} RFI{totalItems === 1 ? '' : 's'}
          </span>
        </CardContent>
      </Card>

      {/* Card 4 – Open RFIs */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-xs font-medium text-muted-foreground">Open RFIs</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center py-3">
          <span className="text-4xl font-bold tabular-nums">{openItems}</span>
          <span className="text-xs text-muted-foreground">awaiting closure</span>
          {overdue > 0 && (
            <span className="mt-1 text-xs font-medium text-red-600">{overdue} overdue</span>
          )}
          {awaiting > 0 && (
            <span className="mt-1 text-xs text-muted-foreground">
              {awaiting} awaiting your response
            </span>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
